import { fg } from '@atlassian/jira-feature-gating';
import {
	type ActiveFieldLayoutItem,
	type AssociatedField,
	type ActiveLayoutItem,
	FIELD_LAYOUT_ITEM_TYPE,
	toLayoutItemKey,
	PANEL_LAYOUT_ITEM_TYPE,
} from '../../types/layout-items.tsx';
import {
	type ActiveLayoutItemOnServer,
	type PanelOnServer,
	type FieldResponse as FieldOnServer,
	FIELD,
	PANEL,
	TAB,
} from '../../types/save-changes.tsx';
import { transformCategory } from './category.tsx';
import { normalizeDefaultValue } from './default-value.tsx';
import { normalizeOptions } from './options.tsx';

export const toField = ({ key, sectionType, data }: FieldOnServer): ActiveFieldLayoutItem => {
	const {
		operations,
		options,
		type,
		custom,
		global,
		required,
		provider,
		properties,
		defaultValue,
	} = data;
	const normalizedOptions = normalizeOptions(type, options);

	const field: AssociatedField = {
		key,
		externalUuid: data.externalUuid,
		name: data.name,
		description: data.description,
		category: transformCategory(sectionType),
		type,
		custom,
		global,
		required,
		operations,
		provider,
		...(fg('forge_custom_fields_on_jsm_portal_create') ? { properties } : {}),
		options: normalizedOptions,
		defaultValue: normalizeDefaultValue(type, defaultValue, normalizedOptions),
		configuration: data.configuration,
		// API doesn't return availability information on update
		// it will be alter reinjected in the SWIFT code
		availability: null,
	};

	return {
		key: toLayoutItemKey(FIELD_LAYOUT_ITEM_TYPE, key),
		layoutItemType: FIELD_LAYOUT_ITEM_TYPE,
		operations,
		category: transformCategory(sectionType),
		data: field,
	};
};

export const toPanel = ({ key, sectionType, data }: PanelOnServer): ActiveLayoutItem => {
	const operations = {
		editable: data.operations.editable,
		removable: data.operations.removable,
		categoriesWhitelist: data.operations.categoriesWhitelist,
		canAssociateInSettings: data.operations.canAssociateInSettings,
		deletable: data.operations.deletable,
	};

	return {
		key: toLayoutItemKey(PANEL_LAYOUT_ITEM_TYPE, key),
		layoutItemType: PANEL_LAYOUT_ITEM_TYPE,
		operations,
		category: transformCategory(sectionType),
		data: {
			key,
			name: data.name,
			operations,
		},
	};
};

export const updateResponseItemTransformers = {
	toField,
	toPanel,
};

export type ItemTransformers = {
	toField: (f: FieldOnServer) => ActiveLayoutItem;
	toPanel: (p: PanelOnServer) => ActiveLayoutItem;
};

/**
 * Transform item data from REST update API into a SWIFT compatible {@link ActiveLayoutItem} objects.
 */
export const updateResponseToActiveLayoutItems = (
	serverItems: ReadonlyArray<ActiveLayoutItemOnServer>,
	itemTransformers: ItemTransformers,
): ActiveLayoutItem[] =>
	serverItems.reduce<ActiveLayoutItem[]>((accum, item) => {
		switch (item.type) {
			case TAB: {
				if (item.items == null || item.items === undefined || item.items.length === 0) {
					return accum;
				}
				const itemsWithSectionType: ReadonlyArray<ActiveLayoutItemOnServer> = item.items
					.map((tabItem) => (tabItem.type === FIELD && Boolean(tabItem.data) ? tabItem : null))
					.filter(Boolean)
					.map(({ data, key, type }) => ({
						data,
						key,
						type,
						sectionType: item.sectionType,
					}));

				return accum.concat(
					updateResponseToActiveLayoutItems(itemsWithSectionType, itemTransformers),
				);
			}
			case FIELD: {
				return accum.concat(itemTransformers.toField(item));
			}
			case PANEL: {
				return accum.concat(itemTransformers.toPanel(item));
			}
			default: {
				return accum;
			}
		}
	}, []);
